import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, Divider, Drawer, Stack} from '@mui/material';
import PropTypes from 'prop-types';
import AdmicityGroupFilterTree from './AdmicityGroupFilterTree';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import IconButton from '@mui/material/IconButton';
import useDynamicHeight from '../utility/hooks/useDynamicHeight';
import {
    useCreateGroupMutation,
    useDeleteGroupMutation,
    useGetCustomGroupFilterQuery,
    useUpdateGroupMutation
} from '../api/services/groupService';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import CreateOrUpdateGroupDialog from './CreateOrUpdateGroupDialog';
import useNotification from '../utility/hooks/useNotification';
import {useGetFormCategoriesQuery} from '../api/services/formCategoriesService';
import {useConfirm} from 'material-ui-confirm';

const groupFiltersDrawerWidth = 350;

const AdmicityDrawer = ({filters, filterId, defaultSelected, onApply, onFilterDeleted}) => {
    const [openGroupFilters, setOpenGroupFilters] = useState(false);
    const [isClosing, setIsClosing] = React.useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [openCreateSubgroupDialog, setOpenCreateSubgroupDialog] = useState(false);
    const [openEditSubgroupDialog, setOpenEditSubgroupDialog] = useState(false);
    const parentRef = useRef(null);
    const freeSpaceHeight = useDynamicHeight(parentRef);
    const [createGroup, createGroupResult] = useCreateGroupMutation();
    const [updateGroup, updateGroupResult] = useUpdateGroupMutation();
    const [deleteGroup] = useDeleteGroupMutation();
    const {data, isLoading, isFetching} = useGetCustomGroupFilterQuery(selectedNode?.nodeId, {
        skip: !openEditSubgroupDialog || !selectedNode
    });
    const {showSuccessNotification, showDetailedErrorNotification} = useNotification();
    const confirm = useConfirm();

    useEffect(() => {
        const isSuccess = createGroupResult.isSuccess || updateGroupResult.isSuccess;
        const isError = createGroupResult.isError || updateGroupResult.isError;
        const error = createGroupResult.error || updateGroupResult.error;

        if (isSuccess) {
            showSuccessNotification('Group saved');
            handleDialogClose();
            createGroupResult.reset();
            updateGroupResult.reset();
        } else if (isError) {
            showDetailedErrorNotification('Failed to save group', error.data.validationErrors);
            createGroupResult.reset();
            updateGroupResult.reset();
        }
    }, [createGroupResult, updateGroupResult]);

    useEffect(() => {
        if (openCreateSubgroupDialog || openEditSubgroupDialog) {
            createGroupResult.reset();
            updateGroupResult.reset();
        }
    }, [openCreateSubgroupDialog, openEditSubgroupDialog]);

    const handleDrawerClose = () => {
        setIsClosing(true);
        setOpenGroupFilters(false);
    };

    const handleDrawerTransitionEnd = () => {
        setIsClosing(false);
    };

    const handleDrawerToggle = () => {
        if (!isClosing) {
            setOpenGroupFilters(!openGroupFilters);
        }
    };

    const handleFilterChange = (nodeId, paths) => {
        onApply(nodeId, paths);
        handleDrawerClose();
    };

    const handleDialogClose = () => {
        setOpenCreateSubgroupDialog(false);
        setOpenEditSubgroupDialog(false);
        setSelectedNode(null);
        createGroupResult.reset();
        updateGroupResult.reset();
    };

    const handleDeleteGroup = async (node) => {
        try {
            await confirm({
                title: 'Confirm Deletion',
                description: (
                    <>
                        Are you sure you want to delete this group?
                        <br /><br />
                        <strong>{node.label}</strong>
                    </>
                ),
                confirmationText: 'Delete',
                cancellationText: 'Cancel',
            });

            await deleteGroup({ groupFilterId: node.nodeId });

            if (onFilterDeleted) {
                onFilterDeleted(node.nodeId);
            }
        } catch {

        }
    };

    const treeItemActions = [
        {
            label: 'Add subgroup',
            onClick: (node) => {
                setOpenCreateSubgroupDialog(true);
                setSelectedNode(node);
            },
        },
        {
            label: 'Edit',
            onClick: (node) => {
                setOpenEditSubgroupDialog(true);
                setSelectedNode(node);
            },
        },
        {
            label: 'Delete',
            onClick: handleDeleteGroup
        },
    ];

    const handleSaveGroup = async (body) => {
        const func = openCreateSubgroupDialog
            ? createGroup
            : openEditSubgroupDialog
                ? updateGroup
                : (_) => {
                };
        const payload = {
            groupName: body.groupName,
            strictRecipientsFiltering: body.strictRecipientsFiltering,
            groupFilters: body.groupFilters.map(x => ({id: x.id, excluded: x.excluded})),
            applicantIds: body.applicationFilters.map(x => x.id),
            studentIds: body.studentFilters.map(x => x.id),
            employeeIds: body.employeeFilters.map(x => x.id),
            enquiryIds: body.enquiryFilters.map(x => x.id),
            formCategoryId: body.groupCategory,
            myGroup: body.groupType,
            membersType: body.membersType
        };

        if (openCreateSubgroupDialog) {
            payload.parentGroupFilterId = selectedNode?.nodeId;
            payload.isDynamic = body.isGroupDynamic;
        }

        if (openEditSubgroupDialog) {
            payload.groupFilterId = selectedNode?.nodeId;
        }

        await func(payload);
    };

    const {data: formCategories = []} = useGetFormCategoriesQuery();
    const formCategoryMap = useMemo(
        () => Object.fromEntries(formCategories.map((category) => [category.id, category])),
        [formCategories]
    );

    return (
        <>
            <Box
                ref={parentRef}
                position="relative"
                sx={{zIndex: 99}}
            >
                <Box sx={{
                    zIndex: 99,
                    display: {
                        xs: 'block',
                        sm: 'block',
                        md: 'block',
                        lg: 'none',
                        xl: 'none',
                    }
                }}>
                    <div>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={handleDrawerToggle}
                            startIcon={<FilterListRoundedIcon/>}
                            sx={{
                                mb: 2,
                                mr: 2,
                                backgroundColor: '#186BBE',
                                boxShadow: 'none',
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': {
                                    backgroundColor: '#155FA7',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            Group Filters
                        </Button>
                    </div>
                    <Drawer
                        variant="temporary"
                        open={openGroupFilters}
                        onTransitionEnd={handleDrawerTransitionEnd}
                        onClose={handleDrawerClose}
                        ModalProps={{keepMounted: true}}
                        sx={{
                            display: {
                                xs: 'block',
                                sm: 'block',
                                md: 'block',
                                lg: 'none',
                                xl: 'none'
                            },
                            '& .MuiDrawer-paper': {
                                boxSizing: 'border-box',
                                width: {
                                    xs: '100%',
                                    sm: '70%',
                                    md: '50%'
                                }
                            }
                        }}
                    >
                        <Box sx={{flexGrow: 1, overflowY: 'auto'}}>
                            <Stack
                                margin={2}
                                direction="row"
                                justifyContent="space-between"
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                >
                                    <IconButton onClick={() => handleDrawerClose()}>
                                        <ArrowBackRoundedIcon/>
                                    </IconButton>
                                    <Typography marginLeft={1} color="text.secondary">Group Filters</Typography>
                                </Stack>
                                <Button
                                    startIcon={<GroupAddRoundedIcon/>}
                                    sx={{color: '#0DAD95', backgroundColor: 'transparent'}}
                                    onClick={() => setOpenCreateSubgroupDialog(true)}
                                >Add Group</Button>
                            </Stack>
                            <Divider sx={{ borderBottom: 'solid 2px #0DAD95' }}/>
                            <AdmicityGroupFilterTree
                                expandMinLevel={0}
                                data={filters}
                                selected={filterId || defaultSelected}
                                onChange={handleFilterChange}
                                itemActions={treeItemActions}
                                formCategoryMap={formCategoryMap}
                                formCategories={formCategories}
                            />
                        </Box>
                        <Divider/>
                    </Drawer>
                </Box>
                <Box sx={{
                    width: groupFiltersDrawerWidth,
                    display: {
                        xs: 'none',
                        sm: 'none',
                        md: 'none',
                        lg: 'block',
                        xl: 'block',
                    }
                }}>
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: {
                                xs: 'none',
                                sm: 'none',
                                md: 'none',
                                lg: 'block',
                                xl: 'block',
                            },
                            '& .MuiDrawer-paper': {
                                display: 'flex',
                                boxSizing: 'border-box',
                                flexDirection: 'column',
                                backgroundColor: '#fff',
                                borderRadius: 1,
                                position: 'relative',
                                width: '100%',
                                maxHeight: `${freeSpaceHeight}px`
                            },
                        }}
                        open
                    >
                        <Box padding={1}>
                            <Button
                                startIcon={<GroupAddRoundedIcon/>}
                                variant="contained"
                                sx={{
                                    backgroundColor: '#ffffff',
                                    boxShadow: 'none',
                                    color: '#0DAD95',
                                    '&:hover': {
                                        backgroundColor: '#f5f6f7',
                                        boxShadow: 'none'
                                    }
                                }}
                                onClick={() => setOpenCreateSubgroupDialog(true)}
                            >Add Group</Button>
                        </Box>

                            <Divider sx={{ borderBottom: 'solid 2px #0DAD95' }} />

                        <Box sx={{flexGrow: 1, overflowY: 'auto'}} marginTop={1}>
                            <AdmicityGroupFilterTree
                                expandMinLevel={0}
                                data={filters}
                                selected={filterId || defaultSelected}
                                onChange={handleFilterChange}
                                itemActions={treeItemActions}
                                formCategoryMap={formCategoryMap}
                                formCategories={formCategories}
                            />
                        </Box>
                    </Drawer>
                </Box>
            </Box>
            {
                openCreateSubgroupDialog || openEditSubgroupDialog
                    ? <CreateOrUpdateGroupDialog
                        open={openCreateSubgroupDialog || openEditSubgroupDialog}
                        title={openCreateSubgroupDialog ? 'Create new group' : 'Update group'}
                        group={
                            selectedNode
                                ? openCreateSubgroupDialog
                                    ? {
                                        parentGroupFilterId: selectedNode.nodeId,
                                        groupType: selectedNode.myGroup,
                                        membersType: selectedNode.membersType,
                                        isDynamic: true
                                    }
                                    : data
                                        ? {
                                            id: data.id,
                                            name: data.name,
                                            parentGroupFilterId: data.basedOnGroupFilterId,
                                            students: data.studentIds,
                                            groupFilters: data.groupFilters,
                                            strictRecipientsFiltering: data.strictRecipientsFiltering,
                                            category: data.formCategoryId,
                                            groupType: data.myGroup,
                                            isDynamic: data.isDynamic,
                                            employeeIds: data.employeeIds,
                                            membersType: data.membersType,
                                            linkedForms: data.linkedForms
                                        }
                                        : {}
                                : {}
                        }
                        formCategories={formCategories}
                        groupFilters={filters?.filter(module =>
                            module.name.toUpperCase() === 'PRE-ADMISSION' ||
                            module.name.toUpperCase() === 'ON-ROLL' ||
                            module.name.toUpperCase() === 'ENQUIRY')}
                        onClose={handleDialogClose}
                        onSave={handleSaveGroup}
                        editMode={openEditSubgroupDialog}
                        isLoading={isLoading || isFetching}
                    />
                    : null
            }
        </>
    );
};

AdmicityDrawer.propTypes = {
    filters: PropTypes.array,
    filterId: PropTypes.number,
    onApply: PropTypes.func,
    onFilterDeleted: PropTypes.func,
    defaultSelected: PropTypes.string
};

export default AdmicityDrawer;